// A `{name}` variable reference. A single brace is the canonical, and only, reference form — it stays
// a reference even when immediately followed by more `}` characters, which are left as literal text
// (an author writing a JSON output template routinely follows a reference with the template's own
// closing brace). A balanced double brace (`{{name}}`) is the one literal escape, which is why that
// alternative is listed first: it wins whenever both could match. A brace immediately preceded by `$`
// or `%` belongs to a different templating syntax and is left alone rather than read as a reference.
//
// Matched without a lookbehind, which not every supported browser has: the preceding character is its
// own capture group instead, and callers check it is empty before treating a match as a reference.
//
// The source is exported rather than a shared RegExp: a global regex carries mutable lastIndex
// state, so callers sharing one instance would get different results depending on who matched last.
// Build your own with createNovaTokenPattern().
export const NOVA_TOKEN_PATTERN_SOURCE = '([$%]?)(\\{\\{\\s*[\\w.:-]+\\s*\\}\\}|\\{\\s*([\\w.:-]+)\\s*\\})';

export function createNovaTokenPattern(): RegExp {
	return new RegExp(NOVA_TOKEN_PATTERN_SOURCE, 'g');
}

// The Markdown structures whose breakage changes how the rest of the document reads. Markdown itself
// has no invalid state, so these are the cases worth surfacing, not a general style check.
export const NovaMarkdownWarning = {
	UnclosedCodeFence: 'UNCLOSED_CODE_FENCE',
	UnclosedInlineCode: 'UNCLOSED_INLINE_CODE',
	UnclosedLinkTarget: 'UNCLOSED_LINK_TARGET',
} as const;
export type NovaMarkdownWarning = (typeof NovaMarkdownWarning)[keyof typeof NovaMarkdownWarning];

export interface NovaTokenOccurrence {
	// The reference in canonical form, braces included and inner whitespace stripped.
	token: string;
	// Offset into the scanned text where the reference begins.
	start: number;
}

export interface NovaMarkdownScan {
	tokens: NovaTokenOccurrence[];
	warnings: NovaMarkdownWarning[];
}

// An opening or closing fence: any amount of leading whitespace, then three or more backticks or
// tildes. The indent is unbounded rather than capped at three spaces so a fenced example nested under
// a list item is still read as a fence — the content this field carries nests fenced examples under
// list items far more often than it writes a genuinely indented code block, and either reading keeps
// that text out of both the reference and structural checks, so the ambiguous case is harmless either
// way.
const FENCE_PATTERN = /^[ \t]*(`{3,}|~{3,})/;

interface ScanState {
	// One flag per character: true where the character is inside a fenced block or an inline code
	// span. A mask rather than a region list keeps the "is this reference inside code" test a direct
	// lookup, and keeps the fence walk and the span walk from having to agree on an interval format.
	codeMask: boolean[];
	warnings: Set<NovaMarkdownWarning>;
}

function markRange(mask: boolean[], start: number, end: number): void {
	for (let index = start; index < end && index < mask.length; index += 1) {
		mask[index] = true;
	}
}

// The index of the next run of exactly `runLength` backticks at or after `from`, or -1. A longer or
// shorter run does not close the span, matching how Markdown pairs code-span delimiters.
function findClosingRun(line: string, from: number, runLength: number): number {
	let index = from;
	while (index < line.length) {
		if (line[index] !== '`') {
			index += 1;
			continue;
		}
		let length = 0;
		while (index + length < line.length && line[index + length] === '`') {
			length += 1;
		}
		if (length === runLength) {
			return index;
		}
		index += length;
	}
	return -1;
}

// Marks the inline code spans on one line into the mask. An opening run with no closing run is left
// as literal text rather than treated as code: a stray backtick in prose is common enough that hiding
// everything after it — including a variable reference — from validation would be worse than the
// unpaired mark itself. Scanning continues past it for both further spans and references.
function markInlineCode(line: string, lineStart: number, state: ScanState): void {
	let index = 0;
	while (index < line.length) {
		if (line[index] !== '`') {
			index += 1;
			continue;
		}
		let runLength = 0;
		while (index + runLength < line.length && line[index + runLength] === '`') {
			runLength += 1;
		}
		const closeIndex = findClosingRun(line, index + runLength, runLength);
		if (closeIndex === -1) {
			state.warnings.add(NovaMarkdownWarning.UnclosedInlineCode);
			index += runLength;
			continue;
		}
		markRange(state.codeMask, lineStart + index, lineStart + closeIndex + runLength);
		index = closeIndex + runLength;
	}
}

// Warns when a link or image target is opened with `](` and never closed on that line. Only text
// outside code spans counts, so `](` inside backticks is example text. Must run after
// markInlineCode for the same line, which is what populates the mask it reads.
//
// A `]` only counts as closing a link label if a `[` opened one earlier on the line, and that opening
// bracket itself only counts when it is not glued to a preceding word character — prose that indexes
// into an array (`a[0]`) reads the same as a link opening once you're only looking at brackets and
// parens, and a real link label is not usually written stuck to the end of an identifier.
function checkLinkTargets(line: string, lineStart: number, state: ScanState): void {
	let labelOpen = false;
	for (let index = 0; index < line.length; index += 1) {
		if (state.codeMask[lineStart + index]) {
			continue;
		}
		if (line[index] === '[' && !/\w/.test(line[index - 1] ?? '')) {
			labelOpen = true;
			continue;
		}
		if (!labelOpen || line[index] !== ']' || line[index + 1] !== '(') {
			continue;
		}
		const closeIndex = line.indexOf(')', index + 2);
		if (closeIndex === -1) {
			state.warnings.add(NovaMarkdownWarning.UnclosedLinkTarget);
			return;
		}
		index = closeIndex;
		labelOpen = false;
	}
}

function findTokens(text: string, codeMask: boolean[]): NovaTokenOccurrence[] {
	const occurrences: NovaTokenOccurrence[] = [];
	const pattern = createNovaTokenPattern();
	let match = pattern.exec(text);
	while (match !== null) {
		const [, prefix, body, name] = match;
		// The name group is only set when the single-brace alternative matched, and an empty prefix
		// means this brace was not borrowed from another templating syntax — both must hold for the
		// match to be a reference.
		if (name !== undefined && prefix === '') {
			const start = match.index + prefix.length;
			const isInCode = codeMask.slice(start, start + body.length).some(Boolean);
			if (!isInCode) {
				occurrences.push({token: `{${name}}`, start});
			}
		}
		match = pattern.exec(text);
	}
	return occurrences;
}

// Reads a Markdown body once and reports both the variable references it contains and the structural
// breakages worth warning about. References inside fenced blocks and inline code spans are treated
// as example text: they are neither reported nor validated, because an author documenting an output
// template is not making a reference. Nothing here rewrites the text.
export function scanMarkdown(text: string): NovaMarkdownScan {
	const state: ScanState = {codeMask: new Array(text.length).fill(false), warnings: new Set()};
	let openFence: string | null = null;
	let offset = 0;
	for (const line of text.split('\n')) {
		const fence = FENCE_PATTERN.exec(line);
		if (openFence !== null) {
			markRange(state.codeMask, offset, offset + line.length);
			// A closing fence must be bare: a line that reopens the fence characters but also carries
			// its own info string (` ```js `, documenting a fenced block rather than ending one) does
			// not close the block.
			if (
				fence &&
				fence[1][0] === openFence[0] &&
				fence[1].length >= openFence.length &&
				line.slice(fence[0].length).trim() === ''
			) {
				openFence = null;
			}
		} else if (fence) {
			openFence = fence[1];
			markRange(state.codeMask, offset, offset + line.length);
		} else {
			markInlineCode(line, offset, state);
			checkLinkTargets(line, offset, state);
		}
		// +1 for the newline the split consumed.
		offset += line.length + 1;
	}
	if (openFence !== null) {
		state.warnings.add(NovaMarkdownWarning.UnclosedCodeFence);
	}
	return {tokens: findTokens(text, state.codeMask), warnings: [...state.warnings]};
}
